import { IConfigSeller } from '@/models/configSeller'
import { webApiService } from '@/services'
import { UTCDateToCountryDate } from '@/utils/countryDate.util'
import { StatusColor } from '@/utils/StatusColor.utility'
import { Button, Pagination, SortDescriptor, Selection } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import XLSX from 'xlsx-js-style'

const useTableDataDinamic = (columns: any, excelName: string, tableType: string, listStatus: number[]) => {
    type LoadDataParams = {
    pageProps: number,
    rowsPerPageProps: number,
    searchProps: string
    listStatusProps?: number[]
  }

    const [data, setData] = useState<{ loading: boolean, loadingExcel: boolean, list: any[], count: number }>({ loading: false, loadingExcel: false, list: [], count: 0 })
    const [page, setPage] = useState(1)
    const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
      column: 'age',
      direction: 'ascending'
    })
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [filterValue, setFilterValue] = useState('')
    const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set([]))
    const filterValueRef = useRef<string>(filterValue)
    const listStatusDinamicRef = useRef(listStatus)
    const sealRef = useRef<string>('none')

    const hasSearchFilter = Boolean(filterValue)

    if (configSeller.config.permisos.ENABLE_CONTRIBUTION !== 1) {
      columns = columns.filter((col: any) => col.uid !== 'contribution_amount')
    }

    useEffect(() => {
      filterValueRef.current = filterValue
    }, [filterValue])

    const pages = Math.ceil(data.count / rowsPerPage)

    const items = data.list

    const sortedItems = useMemo(() => {
      return data.list
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortDescriptor, items, data])

    /* --------------------------------------------------------- */
    const { slug } = useParams()

    const userState = useSelector((store: any) => store.user)

    const loadData = async ({ pageProps, rowsPerPageProps, searchProps, listStatusProps }: LoadDataParams) => {
      setData({ ...data, loading: true })
      let currentListStatus = listStatus
      if (tableType === 'recibidos') {
        currentListStatus = listStatusDinamicRef.current
      }
      await webApiService.getListTradeIn({
        email: userState.email,
        poolId: String(import.meta.env.VITE_COGNITO_POOL_ID),
        listStatus: listStatusProps || currentListStatus,
        id_role: userState.id_role,
        seller: slug,
        page: pageProps || 1,
        limit: rowsPerPageProps,
        searchType: tableType,
        search: searchProps,
        downloadExcel: false,
        seal: sealRef.current
      }).then((response) => {
        setData({ loading: false, list: response.data.info, count: response.data.count, loadingExcel: false })
      })
    }

    useEffect(() => {
      loadData({ pageProps: 1, rowsPerPageProps: 10, searchProps: '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /* --------------------------------------------------------- */
    const onNextPage = useCallback(() => {
      if (page < pages) {
        setPage(page + 1)
        loadData({ pageProps: page + 1, rowsPerPageProps: rowsPerPage, searchProps: filterValue })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pages])

    const onPreviousPage = useCallback(() => {
      if (page > 1) {
        setPage(page - 1)
        loadData({ pageProps: page - 1, rowsPerPageProps: rowsPerPage, searchProps: filterValue })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page])

    const onRowsPerPageChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
      setRowsPerPage(Number(e.target.value))
      loadData({ pageProps: 1, rowsPerPageProps: Number(e.target.value), searchProps: filterValueRef.current })
      setPage(1)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const onSearchChange = (value: any) => {
      setFilterValue(value)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        setPage(1)
        loadData({ pageProps: 1, rowsPerPageProps: rowsPerPage, searchProps: value })
      }, 500)
    }

    const onChangeTab = useCallback((value: any) => {
      console.log('🚀 ~ onChangeTab ~ value:', value)
      listStatusDinamicRef.current = value === 'recibidos' ? [5] : value === 'retirado' ? [7] : [9]
      if (value === 'custodia') sealRef.current = 'false'
      else if (value === 'sello') sealRef.current = 'true'
      else sealRef.current = 'none'
      loadData({ pageProps: 1, rowsPerPageProps: rowsPerPage, searchProps: filterValueRef.current, listStatusProps: value === 'recibidos' ? [5] : value === 'retirado' ? [7] : [9] })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onClear = useCallback(() => {
      setFilterValue('')
      setPage(1)
      loadData({ pageProps: 1, rowsPerPageProps: rowsPerPage, searchProps: '' })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleChangePage = (page: number) => {
      setPage(page)
      loadData({ pageProps: page, rowsPerPageProps: rowsPerPage, searchProps: filterValue })
    }

    const bottomContent = useMemo(() => {
      return (
        <div className='py-2 px-2 flex justify-between items-center'>
          {
            data.loading
              ? <div>loading ...</div>
              : <Pagination
                  isCompact
                  showControls
                  showShadow
                  color='primary'
                  page={page}
                  total={pages}
                  onChange={handleChangePage}
                />
          }
          <div className='hidden sm:flex w-[30%] justify-end gap-2'>
            <Button isDisabled={pages === 1} size='sm' variant='flat' onPress={onPreviousPage}>
              Previous
            </Button>
            <Button isDisabled={pages === 1} size='sm' variant='flat' onPress={onNextPage}>
              Next
            </Button>
          </div>
          <div className='flex justify-between items-center'>
            <span className='text-default-400 text-small mr-3'>Total: {data.count} registros</span>
            <label className='flex items-center text-default-400 text-small'>
              Filas por página:
              <select
                className='bg-transparent outline-none text-default-400 text-small'
                onChange={onRowsPerPageChange}
                value={rowsPerPage}
              >
                <option value='10'>10</option>
                <option value='25'>25</option>
                <option value='50'>50</option>
                <option value='100'>100</option>
              </select>
            </label>
          </div>
        </div>
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length, page, pages, hasSearchFilter, data.loading])

    const exportToCSV = async (type: string) => {
      setData({ ...data, loadingExcel: true })
      const isPrincipal = excelName.toLowerCase().includes('principal')
      if (isPrincipal && !columns.some((ele: any) => ele.uid === 'last_status_date')) {
        columns.push({ name: 'Fecha último estado', uid: 'last_status_date' })
        columns.push({ name: 'Fecha entrega', uid: 'fecha_entregado' })
        columns.push({ name: 'Fecha custodia', uid: 'fecha_custodia' })
        columns.push({ name: 'Fecha Retirado Reuse', uid: 'fecha_retirado' })
        columns.push({ name: 'Usuario que cotizo', uid: 'executive_name' })
        columns.push({ name: 'Usuario que recibio', uid: 'recipient_name' })
        if (configSeller.config.s3.FOLDER_APP === 'ti_coppel') {
          const storeIndex = columns.findIndex((col: any) => col.uid === 'store')
          if (storeIndex !== -1) {
            columns.splice(storeIndex + 1, 0,
              { name: 'Número de tienda', uid: 'seller_store_number' }
            )
          }
          columns.push({ name: 'Folio Coppel', uid: 'folio_coppel' })
          columns.push({ name: 'Fecha Folio', uid: 'fecha_folio_coppel' })
        }
        if (configSeller.config.s3.FOLDER_APP === 'ti_macstore') {
          columns.push({ name: 'SKU', uid: 'final_sku' })
        }
        if (configSeller.config.s3.FOLDER_APP === 'ti_wom') {
          const emailIndex = columns.findIndex((col: any) => col.uid === 'email')
          if (emailIndex !== -1) {
            columns.splice(emailIndex + 1, 0,
              { name: 'RUT', uid: 'rut' },
              { name: 'Documento', uid: 'document_cupon_used' }
            )
          }
        }
      }
      let currentListStatus = listStatus
      if (tableType === 'recibidos') {
        currentListStatus = listStatusDinamicRef.current
      }

      let startDate = ''
      let endDate = ''
      const currentDate = new Date()
      if (type === 'last_week') {
        const lastWeek = new Date()
        lastWeek.setDate(currentDate.getDate() - 7)
        startDate = lastWeek.toISOString().split('T')[0] + ' 00:00:00'
        endDate = currentDate.toISOString().split('T')[0] + ' 23:59:59'
      } else if (type === 'last_month') {
        const lastMonth = new Date()
        lastMonth.setDate(currentDate.getDate() - 30)
        startDate = lastMonth.toISOString().split('T')[0] + ' 00:00:00'
        endDate = currentDate.toISOString().split('T')[0] + ' 23:59:59'
      } else if (type === 'current_month') {
        const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
        startDate = firstDayOfMonth.toISOString().split('T')[0] + ' 00:00:00'
        endDate = currentDate.toISOString().split('T')[0] + ' 23:59:59'
      } else if (type === 'previous_month') {
        const firstDayOfPreviousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
        const lastDayOfPreviousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0)
        startDate = firstDayOfPreviousMonth.toISOString().split('T')[0] + ' 00:00:00'
        endDate = lastDayOfPreviousMonth.toISOString().split('T')[0] + ' 23:59:59'
      }
      const all = type === 'all'
      console.log('🚀 ~ exportToCSV ~ currentListStatus:', currentListStatus, startDate, endDate, all)

      let excelData
      try {
        excelData = await webApiService.getListTradeIn({
          email: userState.email,
          poolId: String(import.meta.env.VITE_COGNITO_POOL_ID),
          listStatus: currentListStatus,
          id_role: userState.id_role,
          seller: slug,
          page: 1,
          limit: 10,
          searchType: tableType,
          search: filterValueRef.current,
          downloadExcel: true,
          seal: sealRef.current,
          all,
          startDate,
          endDate
        })
      } catch (error: any) {
        enqueueSnackbar(error.message, { variant: 'error' })
        setData({ ...data, loadingExcel: false })
        return
      }

      if (type === 'all' && excelData.success === true) {
        // descargar el excelData.data
        const link = document.createElement('a')
        link.href = excelData.data
        link.download = 'reporte_tradein_all.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setData({ ...data, loadingExcel: false })
        return
      }

      const rows = excelData.data.info.map((item: any) => {
        const row: any = {}
        columns.forEach((header: any) => {
          if (header.uid !== 'actions') {
            const isDate = header.uid.includes('fecha') || header.uid.includes('last_status_date')
            if (isDate && item[header.uid] !== null) {
              console.log('🚀 ~ columns.forEach ~ item[header.uid]:', item[header.uid])
              row[header.name] = UTCDateToCountryDate(item[header.uid])
            } else if (header.name === 'Nombre') {
              row[header.name] = item.name + ' ' + item.lastname
            } else {
              row[header.name] = item[header.uid]
            }
          }
        })
        return row
      })

      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(rows)

      // Apply background color to 'status' column
      const statusColumnIndex = columns.findIndex((col: any) => col.uid === 'status') // +1 because Excel columns are 1-based
      if (statusColumnIndex > 0) {
        const range = XLSX.utils.decode_range(String(worksheet['!ref']))
        for (let rowNum = range.s.r + 1; rowNum <= range.e.r; rowNum++) { // Skip header row
          const cellAddress = XLSX.utils.encode_cell({ r: rowNum, c: statusColumnIndex })
          const cellValue = worksheet[cellAddress] ? worksheet[cellAddress].v : null
          if (!worksheet[cellAddress]) continue
          if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {}

          let bgColor = 'ffffff'
          bgColor = StatusColor(cellValue.toUpperCase())
          worksheet[cellAddress].s = {
            fill: { fgColor: { rgb: bgColor.replace('#', '') } },
            font: { color: { rgb: 'ffffff' } }
          }
        }
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, excelName)
      const fileName = excelName + '_' + type + '.xlsx'
      XLSX.writeFile(workbook, fileName)
      setData({ ...data, loadingExcel: false })
    }

    console.log('🚀 ~ useTableDataDinamic ~ page:', page)

    return {
      selectedKeys,
      sortDescriptor,
      filterValue,
      sortedItems,
      bottomContent,
      onSearchChange,
      exportToCSV,
      onClear,
      setSortDescriptor,
      setPage,
      setSelectedKeys,
      loading: data.loading,
      loadingExcel: data.loadingExcel,
      onChangeTab
    }
}

export default useTableDataDinamic
